import "./App.scss";
import { MotionConfig } from "framer-motion";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";

const Home = lazy(() => import("./pages/Home"));
const Newgo = lazy(() => import("./pages/Newgo"));
const Halto = lazy(() => import("./pages/Halto"));
const Flowcam = lazy(() => import("./pages/Flowcam"));
const SoundchartsDevPortal = lazy(() => import("./pages/SoundchartsDevPortal"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DEFAULT_ANCHOR_OFFSET = 96;
const ANCHOR_OFFSETS = {
  about: 120,
  works: 96,
  "cases-study": 96,
};

function AppLayout() {
  const location = useLocation();

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;

    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }
    let targetId;
    try { targetId = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    const scrollToAnchor = () => {
      const element = document.getElementById(targetId);
      if (!element) return false;
      const anchorOffset = Math.max(ANCHOR_OFFSETS[targetId] ?? DEFAULT_ANCHOR_OFFSET, window.innerWidth <= 660 ? 132 : 96);
      window.scrollTo({
        top: Math.max(0, element.getBoundingClientRect().top + window.scrollY - anchorOffset),
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
      });
      return true;
    };
    if (scrollToAnchor()) return;
    // The home route can still be loading when a case study links back to a section.
    const observer = new MutationObserver(() => {
      if (scrollToAnchor()) observer.disconnect();
    });
    observer.observe(document.getElementById("root"), { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      const distanceFromBottom = documentHeight - (scrollY + windowHeight);

      const fadeStart = 200;
      const opacity = Math.max(0, Math.min(1, distanceFromBottom / fadeStart));

      const blurElement = document.querySelector(".gradient-blur");
      if (blurElement) {
        blurElement.style.opacity = opacity;
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // initialize

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="App">
      <a href="#main-content" className="skip-link">Skip to content</a>
      <Header />
      <main id="main-content" tabIndex={-1} key={location.pathname} className="page-transition">
        <Suspense fallback={<div className="route-loading" role="status">Loading project…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/newgo" element={<Newgo />} />
            <Route path="/halto" element={<Halto />} />
            <Route path="/flowcam" element={<Flowcam />} />
            <Route
              path="/soundcharts"
              element={<SoundchartsDevPortal />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <MotionConfig reducedMotion="user">
    <Router>
      <AppLayout />
    </Router>
    </MotionConfig>
  );
}

export default App;
